import type { Client, TransactionReceipt } from 'viem'

import type { ILogLayer } from '@repo/observability/client-logger'
import { ensureError } from '@repo/utils'

import { ACTION_FLOW_EVENTS, ActionFlowError, type WaitContext } from '../types.action-flow'

/**
 * Structured log event names for the callback lifecycle. Owned here because
 * `runWaitCallback` is the sole emitter — promote to a shared
 * `lifecycle-log-events` module if a second call site ever appears.
 */
const WAIT_LOG_EVENTS = {
  before: {
    started: 'action_flow.wait_before_started',
    succeeded: 'action_flow.wait_before_succeeded',
    failed: 'action_flow.wait_before_failed'
  },
  after: {
    started: 'action_flow.wait_after_started',
    succeeded: 'action_flow.wait_after_succeeded',
    failed: 'action_flow.wait_after_failed'
  }
} as const

export interface RunWaitCallbackParams {
  phase: 'before' | 'after'
  callback: (ctx: WaitContext) => Promise<void>
  receipt: TransactionReceipt | null
  publicClient: Client
  chainId: number
  signal: AbortSignal
  setIsCallbackInFlight: (value: boolean) => void
  flowLogger: ILogLayer | undefined
  track: (event: string, properties?: Record<string, unknown>) => void
  flowLabel: string
  stepLabel: string
  stepIndex: number
}

/**
 * Run a `before` / `after` callback with the engine's observability plumbing.
 *
 * The reducer is left untouched per ADR-2026-05-07 §"No new top-level state":
 * the callback-in-flight signal travels through `setIsCallbackInFlight`, a
 * useState bridge that surfaces to `ActionFlowSteps` as a sibling flag next
 * to `state.stepState`. The dialog uses it to swap "Proceed in your wallet"
 * / "Finalizing…" copy for "Waiting…" while the callback is awaiting.
 *
 * Telemetry uses three asymmetric events per phase so dashboards can split
 * outcomes directly without parsing the engine's generic `action_flow_error`
 * message:
 * - `STARTED`   — fires before the callback awaits.
 * - `SUCCEEDED` — fires after a clean return.
 * - `FAILED`    — fires when the callback throws.
 *
 * `STARTED − (SUCCEEDED + FAILED) > 0` is the "callback wedged, never
 * returned" signal — useful for alerting on builders that ignore `signal`.
 * The inner finally keeps `isCallbackInFlight` honest on both paths so the
 * dialog's "Waiting…" copy clears regardless of outcome.
 *
 * On the throw path, non-`ActionFlowError` errors are re-raised with a
 * `[action-flow callback:<phase>]` tag prefix (ADR-2026-05-07 §Observability —
 * "callback-tagged message"), so the engine's surrounding `execution-error`
 * path also lands a filterable string in BetterStack / Sentry / PostHog —
 * useful when the FAILED event isn't enough context. The original error is
 * preserved on `.cause`. `ActionFlowError` is passed through unchanged so its
 * `reportable` / `errorType` survive — the outer catch dispatches on those
 * fields.
 */
export async function runWaitCallback({
  phase,
  callback,
  receipt,
  publicClient,
  chainId,
  signal,
  setIsCallbackInFlight,
  flowLogger,
  track,
  flowLabel,
  stepLabel,
  stepIndex
}: RunWaitCallbackParams): Promise<void> {
  const events =
    phase === 'before'
      ? {
          started: ACTION_FLOW_EVENTS.CALL_BEFORE_STARTED,
          succeeded: ACTION_FLOW_EVENTS.CALL_BEFORE_SUCCEEDED,
          failed: ACTION_FLOW_EVENTS.CALL_BEFORE_FAILED
        }
      : {
          started: ACTION_FLOW_EVENTS.CALL_AFTER_STARTED,
          succeeded: ACTION_FLOW_EVENTS.CALL_AFTER_SUCCEEDED,
          failed: ACTION_FLOW_EVENTS.CALL_AFTER_FAILED
        }
  const logEvents = WAIT_LOG_EVENTS[phase]
  const properties = { flowLabel, chainId, stepIndex, stepLabel }

  setIsCallbackInFlight(true)
  flowLogger?.withMetadata({ stepIndex, stepLabel }).info(logEvents.started)
  track(events.started, properties)

  try {
    try {
      await callback({ receipt, publicClient, chainId, signal })
    } finally {
      // `isCallbackInFlight` must reset on both paths so the dialog's
      // "Waiting…" copy clears regardless of outcome.
      setIsCallbackInFlight(false)
    }
    flowLogger?.withMetadata({ stepIndex, stepLabel }).info(logEvents.succeeded)
    track(events.succeeded, properties)
  } catch (error) {
    flowLogger?.withMetadata({ stepIndex, stepLabel }).error(logEvents.failed)
    track(events.failed, properties)

    if (error instanceof ActionFlowError) throw error
    const original = ensureError(error)
    throw new Error(`[action-flow callback:${phase}] ${original.message}`, { cause: original })
  }
}
